import {
  DatabaseAuto,
  IbulkDelete,
  IbulkDeleteSuccessResponse,
  IdataArrayResponse,
  IdatabaseAuto, IdeleteOneResponse, IfilterProps
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { AuthClient } from '../auth-client';

/**
 * Represents a persisted refresh token record returned by the Auth service.
 *
 * Includes the token string, its owner, expiry, user agent and IP metadata,
 * lifecycle flags (revocation and replacement), creation/update timestamps,
 * and the logical scope this token is valid for.
 *
 * @public
 */
export interface IrefreshToken
  extends IdatabaseAuto {

  /** The user that owns this refresh token. */
  user: string;

  /** The opaque refresh token string. */
  token: string;

  /** Absolute expiration date/time of the token. */
  expiresAt: Date;

  /** Optional user agent captured when the token was issued. */
  userAgent?: string;

  /** Optional IP address captured when the token was issued. */
  ipAddress?: string;

  /** Whether the token has been revoked server-side. */
  isRevoked?: boolean;

  /** If replaced, contains the successor token string. */
  replacedByToken?: string;

  /** The creation timestamp. */
  createdAt: Date;

  /** The last update timestamp. */
  updatedAt: Date;

  /** Logical scope for which the token is valid (e.g., "web", "mobile"). */
  tokenScope: string;

  /** Default number of days before expiry, if provided by the server. */
  tokenDefaultDays?: number;

  /** User info of the user that owns this refresh token. */
  userInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
}


/**
 * Client-side model for interacting with the Refresh Token API.
 *
 * Provides typed helpers to list, filter, fetch, bulk delete, and remove
 * refresh tokens. Each instance wraps a single refresh-token document.
 *
 * @public
 * @remarks
 * This is the raw DTO shape as returned by the API. Prefer using the
 * {@link RefreshToken} class when invoking client operations.
 *
 * @example
 * ```typescript
 * const result = await RefreshToken.getAll();
 *
 * const result = await RefreshToken.filterAll({
 *   // filter criteria
 * });
 *
 * const result = await RefreshToken.bulkDelete({
 *   // delete criteria
 * });
 *
 * const refreshToken = await RefreshToken.getOne('resourceId');
 *
 * const result = await RefreshToken.remove();
 * ```
 */

export class RefreshToken
  extends DatabaseAuto {
  /** Base path for refresh token routes. */
  private static readonly baseUrl = '/refresh-token';

  /** User that owns this refresh token. */
  readonly user: string;

  /** Opaque refresh token string. */
  readonly token: IrefreshToken['token'];

  /** Absolute expiration date/time of the token. */
  readonly expiresAt: IrefreshToken['expiresAt'];

  /** Optional user agent captured when the token was issued. */
  readonly userAgent?: IrefreshToken['userAgent'];

  /** Optional IP address captured when the token was issued. */
  readonly ipAddress?: IrefreshToken['ipAddress'];

  /** Whether the token has been revoked server-side. */
  readonly isRevoked?: IrefreshToken['isRevoked'];

  /** If replaced, contains the successor token string. */
  readonly replacedByToken?: IrefreshToken['replacedByToken'];

  /** Logical scope for which the token is valid. */
  readonly tokenScope: IrefreshToken['tokenScope'];

  /** Default number of days before expiry, if provided by the server. */
  readonly tokenDefaultDays?: IrefreshToken['tokenDefaultDays'];

  /** User info of the user that owns this refresh token. */
  readonly userInfo: IrefreshToken['userInfo'];


  /**
   * Constructs an instance of {@link RefreshToken}.
   *
   * @param data - The refresh-token data used to initialize the instance.
   */
  constructor(data: IrefreshToken) {
    super(data);

    this.user = data.user;
    this.token = data.token;
    this.expiresAt = new Date(data.expiresAt);
    this.userAgent = data.userAgent;
    this.ipAddress = data.ipAddress;
    this.isRevoked = data.isRevoked;
    this.replacedByToken = data.replacedByToken;
    this.tokenScope = data.tokenScope;
    this.tokenDefaultDays = data.tokenDefaultDays;
    this.userInfo = data.userInfo;
  }

  /**
   * Retrieves all refresh tokens with pagination support.
   *
   * @param offset - The starting point for pagination. Defaults to `0`.
   * @param limit - The number of items to retrieve. Defaults to `20`.
   * @returns A promise that resolves to the total count and an array of
   * {@link RefreshToken} instances.
   */
  static async getAll(offset = 0, limit = 20) {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = AuthClient.ehttp
      .makeGet<IdataArrayResponse<IrefreshToken>>(`${RefreshToken.baseUrl}?${query}`);
    const refreshTokens = await lastValueFrom(observer$);

    return {
      count: refreshTokens.count,
      dataArr: refreshTokens.data.map((val) => new RefreshToken(val))
    };
  }

  /**
   * Filters refresh tokens based on the provided filter properties.
   *
   * @param filter - The filter criteria for retrieving refresh tokens.
   * @returns A promise that resolves to the total count and an array of
   * {@link RefreshToken} instances that match the filter.
   */
  static async filterAll(filter: IfilterProps) {
    const observer$ = AuthClient.ehttp
      .makePost<IdataArrayResponse<IrefreshToken>>(`${RefreshToken.baseUrl}/filter`, filter);
    const refreshTokens = await lastValueFrom(observer$);

    return {
      count: refreshTokens.count,
      dataArr: refreshTokens.data.map((val) => new RefreshToken(val))
    };
  }

  /**
   * Retrieves a single refresh token by resource identifier.
   *
   * @param resourceId - The unique identifier for the refresh token to retrieve.
   * @returns A promise that resolves to the {@link RefreshToken} instance.
   */
  static async getOne(resourceId: string) {
    const observer$ = AuthClient.ehttp
      .makeGet<IrefreshToken>(`${RefreshToken.baseUrl}/${resourceId}`);
    const refreshToken = await lastValueFrom(observer$);

    return new RefreshToken(refreshToken);
  }

  /**
   * Removes multiple refresh tokens based on the provided criteria.
   *
   * @param payload - The criteria for deleting multiple refresh tokens.
   * @returns A promise that resolves to the bulk delete operation result.
   */
  static bulkDelete(payload: IbulkDelete) {
    const observer$ = AuthClient.ehttp
      .makePost<IbulkDeleteSuccessResponse>(`${RefreshToken.baseUrl}/bulk-delete`, payload);

    return lastValueFrom(observer$);
  }

  /**
   * Revokes the current refresh token instance.
   *
   * @returns A promise that resolves to the delete operation result, or `null`.
   */
  revoke() {
    const observer$ = AuthClient.ehttp
      .makePost<IdeleteOneResponse | null>(`${RefreshToken.baseUrl}/revoke`, { _id: this._id });

    return lastValueFrom(observer$);
  }

  /**
   * Removes the current refresh token instance.
   *
   * @returns A promise that resolves to the delete operation result, or `null`.
   */
  remove() {
    const observer$ = AuthClient.ehttp
      .makeDelete<IdeleteOneResponse | null>(`${RefreshToken.baseUrl}/${this._id}`);

    return lastValueFrom(observer$);
  }
}
